/**
 * Widget yükleyicisi (gömme kodu). Görevleri: yapılandırmayı okumak, Shadow DOM'da balonu çizmek,
 * ilk tıklamada sohbet penceresini tembel yüklemek. Sayfanın yüklenmesini engellemez (async).
 */
import { PRODUCT_NAME, type Translate } from '@destera/i18n';
import { applyCustomCss } from '../shared/custom-css.ts';
import { launcherCatalog, type LauncherMessages } from '@destera/i18n/catalogs/launcher';
import type { ChatHandle, ChatModule } from '../shared/chat-module.ts';
import { refreshBrand, storedBrand } from '../shared/appearance.ts';
import { positionOf, readConfig, type WidgetBrand, type WidgetConfig } from '../shared/config.ts';
import { themeCss, themeOf, WINDOW_SHADOWS } from '../shared/theme.ts';
import { createLauncher, LAUNCHER_CSS } from './launcher.ts';
import { startTracker, type Tracker } from './tracker/index.ts';

const HOST_TAG = 'destera-widget';

function loadChatModule(script: HTMLScriptElement | null): Promise<ChatModule> {
  if (import.meta.env.DEV) return import('../chat/index.tsx');
  // Üretimde sohbet penceresi yükleyicinin yanındaki ayrı dosyadır; göreli değil, mutlak adresle alınır.
  const base = script?.src ?? location.href;
  return import(/* @vite-ignore */ new URL('chat.js', base).href);
}

function boot(script: HTMLScriptElement | null): void {
  if (document.querySelector(HOST_TAG)) return; // iki kez gömülürse ikinci yok sayılır
  const parsed = readConfig((name) => script?.dataset[name], navigator.languages);
  if (!parsed.ok) {
    // Sitenin sahibine görünür uyarı; widget hiç çizilmez.
    // oxlint-disable-next-line no-console -- gömme kodu hatası yalnız konsolda bildirilebilir
    console.warn(`[${PRODUCT_NAME}] ${parsed.problems.join('; ')}`);
    return;
  }
  const { config } = parsed;
  /**
   * Marka: SAKLANAN hâli anında uygulanıyor, tazesi arka planda isteniyor.
   *
   * Balonu markayı bekleterek çizmek, en hafif widget iddiasını bozardı — ziyaretçi bir ağ
   * turu boyunca hiçbir şey görmezdi. İlk ziyarette balon ürün varsayılanıyla (ya da gömme
   * kodundaki renkle) çiziliyor, sonraki açılışlarda ilk karede doğru renkte.
   *
   * **Okuma İZLEYİCİDEN ÖNCE ve bu bir DÜZELTME:** izleyici yüzeylerin temasını taşıyor ve
   * eskiden `config.accent` ile başlıyordu — yani panelden rengini değiştiren işletmenin
   * balonu doğru, etkileşim pencereleri ESKİ renkteydi. Stil tokenları da (köşe, gölge, yazı
   * tipi) yalnız markada duruyor, yani o sırada hiç okunmuyordu.
   */
  const cached = config.demo ? undefined : storedBrand(config.workspaceId);
  const painted: WidgetConfig =
    cached === undefined
      ? config
      : {
          ...config,
          accent: cached.accent,
          brand: cached,
          position: positionOf(cached.position, config.position),
        };
  /**
   * İzleyici balondan ÖNCE başlar: sayfa görüntüleme, balonun metinleri yüklenmesini beklerse
   * hızlı geçilen sayfalarda hiç kaydedilmez. Demo modunda hiç başlamaz — önizleme trafiği
   * gerçek ziyaretçi değildir.
   */
  const tracker =
    config.track && !config.demo
      ? startTracker({
          workspaceId: config.workspaceId,
          ingestBase: config.ingestBase,
          apiBase: config.apiBase,
          locale: config.locale,
          theme: themeOf(painted.accent, painted.brand),
          ...(script?.src !== undefined && { scriptSrc: script.src }),
        })
      : undefined;

  /**
   * Tazeleme her açılışta isteniyor ama ağa HER AÇILIŞTA çıkmıyor: yanıt beş dakika
   * önbellekli ve ikinci istek tarayıcının kendi deposundan dönüyor.
   *
   * **İlk ziyarette taze marka BU açılışta uygulanıyor.** Depoda marka yokken balon ürün
   * varsayılanıyla çiziliyor (bekletmek yok) ve marka gelince yerinde düzeltiliyor: renk,
   * etiket ve henüz açılmamış pencerenin başlığı/karşılaması. Eskiden yalnız saklanıyordu ve
   * işletmenin sitesine ilk gelen herkes mor balonla "Asistan"ı görüyordu (2026-10-08).
   */
  const fresh = config.demo ? undefined : refreshBrand(config.apiBase, config.workspaceId);

  // Balonun metinleri yükleyiciye gömülüdür; bu çağrı ağa çıkmaz.
  void launcherCatalog.load(config.locale).then((t) => {
    const rebrand = mount(painted, t, script, tracker);
    if (cached === undefined) {
      void fresh?.then((brand) => {
        if (brand !== undefined) rebrand(brand);
        return undefined;
      });
    }
    return undefined;
  });
}

/** Balonu çizer; dönen işlev, sonradan gelen markayı yerinde uygular. */
function mount(
  initial: WidgetConfig,
  t: Translate<LauncherMessages>,
  script: HTMLScriptElement | null,
  tracker: Tracker | undefined,
): (brand: WidgetBrand) => void {
  let config = initial;
  const host = document.createElement(HOST_TAG);
  const root = host.attachShadow({ mode: 'open' });
  const style = document.createElement('style');
  const paint = () => {
    style.textContent = `${LAUNCHER_CSS}${themeCss(themeOf(config.accent, config.brand), WINDOW_SHADOWS)}`;
  };
  paint();
  root.append(style);
  /** İşletmenin CSS'i EN SONA: ürünün stilinden sonra gelen kural kazanıyor. */
  applyCustomCss(root, config.brand?.customCss);
  document.body.append(host);

  let chat: Promise<ChatHandle> | undefined;
  let open = false;

  /** Pencereyi baloncuğa indirir (kapatma düğmesi, Esc ve sayfaya tıklama aynı yolu kullanır). */
  const minimize = () => {
    if (!open) return;
    open = false;
    tracker?.note('widget_close');
    document.removeEventListener('pointerdown', onOutside, true);
    /**
     * Odak widget'ın İÇİNDEYSE balona dönüyor.
     *
     * Kullanıcı sayfada başka bir yere geçtiyse odağı geri çalmak, okuduğu yerden koparmak
     * olurdu — kapanış her zaman kullanıcının isteğiyle olmuyor (dışarı tıklama da kapatıyor).
     */
    const wasInside = root.activeElement !== null;
    void chat?.then((handle) => handle.hide());
    launcher.show();
    if (wasInside) launcher.focus();
  };

  /**
   * Sayfanın boşluğuna tıklamak pencereyi kapatır. Ölçüt gölge kök (host) öğesidir: widget'ın
   * kendi içine yapılan tıklama (baloncuk dahil) dışarı sayılmaz.
   */
  function onOutside(event: PointerEvent): void {
    if (!event.composedPath().includes(host)) minimize();
  }

  /**
   * Sohbeti açan tek yol.
   *
   * Hem balon hem proaktif yüzeyler bunu çağırıyor: ikinci bir açılış yolu, birinin
   * ötekinin durumunu (okunmamış sayacı, dinleyiciler) atlaması demek olurdu.
   */
  const openChat = (): void => {
    chat ??= loadChatModule(script).then((module) =>
      module.mount(root, config, {
        onClose: minimize,
        onUnread: (count) => launcher.setUnread(count),
      }),
    );
    launcher.hide();
    open = true;
    tracker?.note('widget_open');
    // Açılışı tetikleyen tıklama dinleyiciye düşmesin diye bir sonraki kareye bırakılır.
    requestAnimationFrame(() => {
      if (open) document.addEventListener('pointerdown', onOutside, true);
    });
    chat
      .then((handle) => handle.show())
      .catch(() => {
        open = false;
        launcher.show();
      });
  };

  const launcher = createLauncher(root, config, t, openChat);

  // Yüzeyler sohbeti balonun kendi yolundan açıyor.
  tracker?.connectChat(openChat, t('close'));

  /**
   * Sonradan gelen marka: renk ve etiket hemen; pencere henüz yüklenmediyse (ilk tıklama
   * çoğu zaman markadan sonra) başlık ve karşılama da. Açık pencereye dokunulmuyor — konuşma
   * sürerken başlığın değişmesi, ziyaretçiye başka biriyle konuşuyormuş hissi verirdi.
   */
  return (brand) => {
    config = {
      ...config,
      accent: brand.accent,
      brand,
      position: positionOf(brand.position, initial.position),
    };
    paint();
    launcher.setPosition(config.position);
    applyCustomCss(root, brand.customCss);
    launcher.setLabel(brand.launcherLabel ?? t('open'));
  };
}

// Klasik gömme kodunda `currentScript` doludur; modül olarak yüklenirse (geliştirme) özniteliğe göre bulunur.
const currentScript =
  document.currentScript instanceof HTMLScriptElement
    ? document.currentScript
    : document.querySelector<HTMLScriptElement>('script[data-workspace]');
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => boot(currentScript), { once: true });
} else {
  boot(currentScript);
}
